/* ============================================================
   CART REWARD BAR
   Barra de progreso + auto-add de regalo en el cart drawer.

   Configuración: editar solo CONFIG.
   Fuente de verdad: /cart.js (no el DOM).
   Concurrencia: cola de promesas para evitar dobles llamadas.
   Eventos: se suscribe a PUB_SUB_EVENTS.cartUpdate de Dawn.
   ============================================================ */
(function () {
  'use strict';

  const CONFIG = {
    GIFT_VARIANT_ID: 47057279385785,
    GOAL_CENTS: 7999000,                  // $79.990 CLP en centavos
    CURRENCY_FORMATTER: new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    }),
    TEXT_REMAINING: (formatted) => `Compra ${formatted} más para obtener un regalo`,
    TEXT_REACHED: 'Agregamos un regalo a tu pedido',
    DEBOUNCE_MS: 150,
  };

  /* --- Estado interno --- */
  let queue = Promise.resolve();
  let debounceTimer = null;
  let initialized = false;

  /* --- Helpers --- */
  const getCart = () =>
    fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());

  const isGiftLine = (item) =>
    item.variant_id === CONFIG.GIFT_VARIANT_ID ||
    (item.properties && item.properties._gift);

  const computeNonGiftTotal = (cart) =>
    cart.items.reduce(
      (sum, item) => (isGiftLine(item) ? sum : sum + item.final_line_price),
      0
    );

  const countGifts = (cart) => cart.items.filter(isGiftLine).length;
  const getGiftKeys = (cart) => cart.items.filter(isGiftLine).map((i) => i.key);

  /* --- Cola de operaciones --- */
  const enqueue = (fn) => {
    queue = queue.then(fn).catch((err) => {
      console.error('[RewardBar]', err);
    });
    return queue;
  };

  /* --- Mutaciones de carrito --- */
  const addGift = () =>
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: CONFIG.GIFT_VARIANT_ID,
            quantity: 1,
            properties: { _gift: 'true' },
          },
        ],
      }),
    });

  const removeByKey = (key) =>
    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });

  /* --- Render UI --- */
  const renderUI = (totalCents, goalReached) => {
    const fill = document.getElementById('CartDrawer-RewardFill');
    const text = document.getElementById('CartDrawer-RewardText');
    const icon = document.getElementById('CartDrawer-RewardIcon');
    if (!fill || !text || !icon) return;

    const pct = Math.min(100, Math.max(0, (totalCents / CONFIG.GOAL_CENTS) * 100));
    fill.style.width = `${pct}%`;

    if (goalReached) {
      text.textContent = CONFIG.TEXT_REACHED;
      icon.classList.add('is-reached');
    } else {
      const remainingCents = CONFIG.GOAL_CENTS - totalCents;
      const remainingFormatted = CONFIG.CURRENCY_FORMATTER.format(
        Math.ceil(remainingCents / 100)
      );
      text.textContent = CONFIG.TEXT_REMAINING(remainingFormatted);
      icon.classList.remove('is-reached');
    }
  };

  /* --- Refresh del drawer (sin re-disparar sync) --- */
  const refreshDrawer = () => {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(`${cartUrl}?section_id=cart-drawer`, {
      credentials: 'same-origin',
    })
      .then((r) => r.text())
      .then((html) => {
        const newDrawerInner = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.drawer__inner');
        const currentDrawerInner = document.querySelector(
          'cart-drawer .drawer__inner'
        );
        if (newDrawerInner && currentDrawerInner) {
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }
        return fetch('/?section_id=cart-icon-bubble', {
          credentials: 'same-origin',
        });
      })
      .then((r) => r && r.text())
      .then((html) => {
        if (!html) return;
        const newBubble = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.shopify-section');
        const currentBubble = document.querySelector(
          '#cart-icon-bubble .shopify-section'
        );
        if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML;
      });
  };

  /* --- Sync principal --- */
  const performSync = async () => {
    try {
      const cart = await getCart();
      const nonGiftTotal = computeNonGiftTotal(cart);
      const goalReached = nonGiftTotal >= CONFIG.GOAL_CENTS;
      const giftCount = countGifts(cart);

      // Render visual primero
      renderUI(nonGiftTotal, goalReached);

      let mutated = false;

      if (goalReached && giftCount === 0) {
        // Caso 1: meta alcanzada y no hay regalo → agregar
        await addGift();
        mutated = true;
      } else if (goalReached && giftCount > 1) {
        // Caso 2: meta alcanzada y hay duplicados → dejar uno
        const keys = getGiftKeys(cart);
        for (let i = 1; i < keys.length; i++) {
          await removeByKey(keys[i]);
        }
        mutated = true;
      } else if (!goalReached && giftCount > 0) {
        // Caso 3: meta no alcanzada y hay regalos → eliminar todos
        const keys = getGiftKeys(cart);
        for (const key of keys) {
          await removeByKey(key);
        }
        mutated = true;
      }

      if (mutated) {
        await refreshDrawer();
        // Después del refresh, releer y volver a renderizar
        const freshCart = await getCart();
        const freshTotal = computeNonGiftTotal(freshCart);
        renderUI(freshTotal, freshTotal >= CONFIG.GOAL_CENTS);
      }
    } catch (err) {
      console.error('[RewardBar] sync error:', err);
    }
  };

  /* --- API expuesta --- */
  const sync = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      enqueue(performSync);
    }, CONFIG.DEBOUNCE_MS);
  };

  const init = () => {
    if (initialized) return;
    initialized = true;

    sync();

    // Suscripción al pubsub de Dawn — reacciona a TODOS los cambios de carrito
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, sync);
    }

    // Cuando se abre el drawer (por si el cliente lo abre antes del primer sync)
    document.addEventListener('click', (e) => {
      const cartIcon = e.target.closest('#cart-icon-bubble');
      if (cartIcon) sync();
    });
  };

  // Exponer globalmente para debug y para que el cart-drawer pueda llamarlo
  window.CartRewardBar = { sync, init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();